import type { Component, JSX } from 'solid-js'
import { For, Show } from 'solid-js'
import { Trans } from '@mbarzda/solid-i18next'
import { Icon } from '@iconify-icon/solid'
import { useNumberFormatter } from '../../shared/helpers/currency-formatter'

interface MetricBreakdownItem {
  label: string
  value: number
}

interface DashboardMetricCardProps {
  titleKey: string
  value: number
  diff: number
  icon: JSX.Element
  format?: 'currency' | 'number' | 'percentage'
  variant?: 'card' | 'none'
  iconBgClass?: string
  iconColorClass?: string
  class?: string
  breakdown?: MetricBreakdownItem[]
}

const DashboardMetricCard: Component<DashboardMetricCardProps> = (props) => {
  const { numberFormatter, percentageFormatter, currencyFormatter } = useNumberFormatter()

  const hasBreakdown = () => (props.breakdown?.length ?? 0) >= 1

  const formatValue = (): string => {
    switch (props.format) {
      case 'currency':
        return currencyFormatter(props.value, 0)
      case 'percentage':
        return percentageFormatter(props.value) ?? ''
      case 'number':
      default:
        return numberFormatter(props.value) ?? ''
    }
  }

  const formatDiff = (): string => {
    const absDiff = Math.abs(props.diff)
    return percentageFormatter(absDiff) ?? ''
  }

  const isPositive = () => props.diff >= 0

  const Content = () => (
    <div class="flex items-center gap-4">
      <div
        class={`flex h-12 w-12 items-center justify-center rounded-lg aspect-square ${
          props.iconBgClass ?? 'bg-primary-400'
        }`}
      >
        <div class={props.iconColorClass ?? 'text-white'}>{props.icon}</div>
      </div>
      <div class="flex flex-col overflow-hidden w-full">
        <p class="text-sm font-medium text-gray-500 whitespace-nowrap truncate text-ellipsis flex items-center gap-1">
          <Trans key={props.titleKey} />
          <Show when={hasBreakdown()}>
            <span class="text-gray-400 transition-transform duration-200 group-hover:rotate-180">
              <Icon icon="heroicons:chevron-down-20-solid" class="text-[0.75rem]" />
            </span>
          </Show>
        </p>
        <div class="flex items-baseline gap-2">
          <h3 class="text-2xl font-semibold text-gray-900">{formatValue()}</h3>
          <Show when={props.diff !== 0}>
            <div class={`flex items-center text-sm font-medium ${isPositive() ? 'text-green-600' : 'text-red-600'}`}>
              <Show when={isPositive()} fallback={'↓'}>
                {'↑'}
              </Show>
              <span class="ml-1">{formatDiff()}</span>
            </div>
          </Show>
        </div>
      </div>
    </div>
  )

  const Breakdown = () => (
    <div class="absolute left-0 right-0 top-full pt-2 z-30 opacity-0 -translate-y-1 pointer-events-none transition-all duration-200 ease-in-out group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto">
      <div class="bg-white rounded-lg shadow-md border border-gray-200">
        <div class="flex items-stretch px-2 py-2.5">
          <For each={props.breakdown}>
            {(item, index) => (
              <>
                <Show when={index() > 0}>
                  <div class="w-px bg-gray-100 mx-2" />
                </Show>
                <div class="flex-1 flex flex-col items-center justify-center text-center gap-0.5 px-2">
                  <span class="text-sm font-bold text-gray-900">{numberFormatter(item.value) ?? '0'}</span>
                  <span class="text-[10px] font-medium text-gray-500 leading-tight">{item.label}</span>
                </div>
              </>
            )}
          </For>
        </div>
      </div>
    </div>
  )

  return (
    <Show when={props.variant !== 'none'} fallback={<Content />}>
      <div class="relative group">
        <div
          class={`rounded-lg border border-gray-200 bg-white p-6 shadow-sm flex flex-col gap-4 ${
            hasBreakdown() ? 'cursor-pointer' : ''
          } ${props.class ?? ''}`}
        >
          <Content />
        </div>
        <Show when={hasBreakdown()}>
          <Breakdown />
        </Show>
      </div>
    </Show>
  )
}

export default DashboardMetricCard
